import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The help & support launcher — ONE button, same look in platform-app,
 * super-admin-ui and the POS (owner mockup v4, 2026-09-29, option D2).
 *
 *  - 28px ruby rounded square with the standard Lucide gem icon (owner,
 *    2026-09-29: the brand gem read too bulky). 44px hit area via an
 *    invisible 8px ring.
 *  - Static. The gem draws itself ONCE on hover (pointer devices only). It
 *    never loops: the looping gem means "loading" everywhere else.
 *  - Portalled to <body> at Z_HELP, above every page modal and full-screen
 *    editor, so a problem can be reported from anywhere.
 *  - Sits 10px into the bottom-right corner, and lifts itself above whatever
 *    pinned bar / toast / fixed control is under that corner (see
 *    useCornerClearance) — no page has to know the launcher exists.
 */

export const Z_HELP = 2147483000;
export const HELP_LAUNCHER_ATTR = 'data-help-launcher';

const SIZE = 28;
const INSET = 10;
const GAP = 8;

/** Lucide "gem" (the icon set every app already uses), inlined so each
 *  stroke can carry pathLength=1 for the draw-once hover. */
const GEM_PATHS = ['M6 3h12l4 6-10 13L2 9Z', 'M11 3 8 9l4 13 4-13-3-6', 'M2 9h20'];
const GEM_PX = 15;

const CSS = `
.rt-help{position:fixed;width:${SIZE}px;height:${SIZE}px;border-radius:8px;border:0;padding:0;margin:0;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:#DC143C;color:#fff;
  box-shadow:0 2px 6px -2px rgba(159,18,57,.5),inset 0 0 0 1px rgba(255,255,255,.12);
  transition:bottom .15s ease,background-color .15s ease,transform .15s ease;-webkit-tap-highlight-color:transparent}
.rt-help::before{content:"";position:absolute;inset:-8px;border-radius:14px}
.rt-help:hover{background:#9F1239}
.rt-help:active{transform:scale(.94)}
.rt-help:focus-visible{outline:2px solid #DC143C;outline-offset:3px}
.rt-help.rt-rec{background:#991B1B}
.rt-help .rt-q,.rt-help .rt-n{position:absolute;display:flex;align-items:center;justify-content:center;background:#fff;color:#9F1239;
  font:800 8.5px/1 system-ui,-apple-system,"Segoe UI",sans-serif;box-shadow:0 0 0 1.5px #DC143C;pointer-events:none}
.rt-help .rt-q{right:-4px;bottom:-4px;width:12px;height:12px;border-radius:50%}
.rt-help .rt-n{right:-5px;top:-5px;min-width:14px;height:14px;padding:0 3px;border-radius:7px;font-weight:700;font-variant-numeric:tabular-nums}
.rt-help .rt-dot{position:absolute;left:4px;top:4px;width:6px;height:6px;border-radius:50%;background:#fff}
@media (hover:hover) and (prefers-reduced-motion:no-preference){
  .rt-help:hover svg path{stroke-dasharray:1;animation:rtHelpCut .6s cubic-bezier(.2,.8,.3,1) both;animation-delay:calc(var(--i) * 90ms)}
}
@keyframes rtHelpCut{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
`;

const HelpGem: React.FC = () => (
  <svg viewBox="0 0 24 24" width={GEM_PX} height={GEM_PX} fill="none" stroke="currentColor" strokeWidth={2.25}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {GEM_PATHS.map((d, i) => (
      <path key={i} d={d} pathLength={1} style={{ ['--i' as any]: i } as React.CSSProperties} />
    ))}
  </svg>
);

/**
 * Keeps the launcher off whatever sits in the bottom-right corner, on every
 * page, without that page knowing about it. Samples the launcher's resting
 * box; for the first element under each point:
 *   - inside a small fixed/sticky container (save bar, toast, deploy widget,
 *     page FAB)            -> lift above that container
 *   - an interactive control that does NOT scroll (POS Pay button, a static
 *     footer button)       -> lift above that control
 *   - scrolling content, or a full-screen overlay's backdrop -> stay put
 */
const INTERACTIVE = 'button,a[href],input,select,textarea,[role="button"],[role="tab"],[tabindex]:not([tabindex="-1"])';

function obstructionTop(el: Element, vw: number, vh: number): number | null {
  let scrolls = false;
  for (let n: Element | null = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.position === 'fixed' || cs.position === 'sticky') {
      const r = n.getBoundingClientRect();
      const fullScreen = r.width > vw * 0.6 && r.height > vh * 0.6;
      if (!fullScreen) return r.top;
      break; // inside a full-screen overlay/editor: judge the control itself
    }
    if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) scrolls = true;
  }
  const ctl = el.closest(INTERACTIVE);
  if (ctl && !scrolls) return ctl.getBoundingClientRect().top;
  return null;
}

export function useCornerClearance(enabled: boolean): number {
  const [bottom, setBottom] = useState(INSET);
  useEffect(() => {
    if (!enabled || typeof document.elementsFromPoint !== 'function') return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = vw - INSET - SIZE;
      const top = vh - INSET - SIZE;
      const pts: Array<[number, number]> = [
        [left + 2, top + 2], [left + SIZE - 2, top + 2], [left + SIZE / 2, top + SIZE / 2],
        [left + 2, top + SIZE - 2], [left + SIZE - 2, top + SIZE - 2],
      ];
      let minTop = Infinity;
      for (const [x, y] of pts) {
        const hit = document.elementsFromPoint(x, y).find((e) => !e.closest(`[${HELP_LAUNCHER_ATTR}]`));
        if (!hit) continue;
        const t = obstructionTop(hit, vw, vh);
        if (t !== null && t < minTop) minTop = t;
      }
      let next = INSET;
      if (minTop !== Infinity) {
        const lifted = vh - minTop + GAP;
        // Something taller than ~45% of the screen is not a "bar" — stay put.
        if (lifted > INSET && lifted < vh * 0.45) next = Math.round(lifted);
      }
      setBottom((b) => (b === next ? b : next));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    schedule();
    const timer = window.setInterval(schedule, 700);
    window.addEventListener('resize', schedule);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled]);
  return bottom;
}

/** Bottom offset for a panel that opens above the launcher. */
export const panelBottom = (launcherBottom: number) =>
  `calc(max(${launcherBottom}px, env(safe-area-inset-bottom)) + ${SIZE + GAP}px)`;

interface Props {
  awaitingRetestCount?: number;
  onClick: () => void;
  label?: string;
  /** Tutorial recording in progress with its panel closed — steady dot +
   *  step count instead of the attention count. Never blinks. */
  recording?: boolean;
  recordingSteps?: number;
  /** Hide while the launcher's own report modal / wizard is open. */
  hidden?: boolean;
  /** Reports the current bottom offset so menus can open above it. */
  onBottomChange?: (bottom: number) => void;
}

const ReportIssueButton: React.FC<Props> = ({
  awaitingRetestCount = 0,
  onClick,
  label = 'Help & support',
  recording = false,
  recordingSteps = 0,
  hidden = false,
  onBottomChange,
}) => {
  const bottom = useCornerClearance(!hidden);
  useEffect(() => { onBottomChange?.(bottom); }, [bottom, onBottomChange]);

  if (hidden || typeof document === 'undefined') return null;
  const badge = recording ? recordingSteps : awaitingRetestCount;

  return createPortal(
    <>
      <style>{CSS}</style>
      <button
        type="button"
        {...{ [HELP_LAUNCHER_ATTR]: '' }}
        onClick={onClick}
        title={recording ? `Recording a tutorial (${recordingSteps} steps) — click to open` : label}
        aria-label={label}
        className={`rt-help${recording ? ' rt-rec' : ''}`}
        style={{
          zIndex: Z_HELP,
          right: `max(${INSET}px, env(safe-area-inset-right))`,
          bottom: `max(${bottom}px, env(safe-area-inset-bottom))`,
        }}
      >
        <HelpGem />
        {recording && <span className="rt-dot" />}
        {badge > 0 ? <span className="rt-n">{badge > 9 ? '9+' : badge}</span> : !recording && <span className="rt-q">?</span>}
      </button>
    </>,
    document.body,
  );
};

export default ReportIssueButton;
